import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { CersDecisionStatus, StaffCersTrueUpSubmittalsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_CERS_TRUE_UP_SUBMITTALS } from "hooks/useStaffCersTrueUpSubmittals";
import { useCallback, useMemo, useState } from "react";

import {
  decisionStatusColor,
  formatDate,
  sortModulesNotAcceptedFirst,
} from "../Submittals/cersStatus";
import { SubmittalDetailDrawer } from "../Submittals/SubmittalDetailDrawer";

type Row = StaffCersTrueUpSubmittalsQuery["staffCersTrueUpSubmittals"]["items"][number];

// Stable, module-level config (V2 requires stable prop references)
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "submittedDate", sort: "desc" }];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Facility", fields: ["facilityName"] },
  { name: "Organization", fields: ["organizationName"] },
];

const MAX_VISIBLE_MODULES = 3;

const getItems = (data: StaffCersTrueUpSubmittalsQuery) =>
  data.staffCersTrueUpSubmittals.items ?? [];
const getCount = (data: StaffCersTrueUpSubmittalsQuery) =>
  data.staffCersTrueUpSubmittals.count ?? 0;

export function TrueUpSection() {
  const [selected, setSelected] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "facilityName", headerName: "Facility", flex: 0.7 },
      { field: "organizationName", headerName: "Organization", flex: 0.7 },
      { field: "localRegulatorDistrict", headerName: "CUPA", flex: 0.5, sortable: false },
      {
        field: "submittedDate",
        headerName: "Submitted Date",
        flex: 0.4,
        valueGetter: (_, row) => formatDate(row.submittedDate),
      },
      { field: "submittedBy", headerName: "Submitted By", flex: 0.5, sortable: false },
      {
        field: "moduleDecisions",
        headerName: "Modules",
        flex: 1.5,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.moduleDecisions?.length) return null;
          // Not-accepted first so rejections are never the ones clipped; show a
          // capped set on one line (+N overflow) and list the full set on hover,
          // matching the Submittals grid.
          const ordered = sortModulesNotAcceptedFirst(row.moduleDecisions);
          const visible = ordered.slice(0, MAX_VISIBLE_MODULES);
          const overflowCount = ordered.length - visible.length;
          const tooltip = (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {ordered.map((decision) => (
                <span key={decision.id}>
                  {prettyPrintEnumValue(decision.module)} — {prettyPrintEnumValue(decision.status)}
                </span>
              ))}
            </Box>
          );
          return (
            <Tooltip title={tooltip}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
                <Box sx={{ display: "flex", gap: 0.5, overflow: "hidden", minWidth: 0 }}>
                  {visible.map((decision) => (
                    <StatusChip
                      key={decision.id}
                      label={prettyPrintEnumValue(decision.module)}
                      color={decisionStatusColor(decision.status)}
                    />
                  ))}
                </Box>
                {overflowCount > 0 && (
                  <StatusChip label={`+${overflowCount}`} color="default" sx={{ flexShrink: 0 }} />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "modulesDecidedCount",
        headerName: "Modules Decided",
        flex: 0.4,
        sortable: false,
        valueGetter: (_, row) =>
          row.moduleDecisions?.filter(
            (d) =>
              d.status !== CersDecisionStatus.Submitted &&
              d.status !== CersDecisionStatus.UnderReview
          ).length ?? 0,
      },
    ],
    []
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const onRowClick = useCallback((params: GridRowParams<Row>) => setSelected(params.row), []);

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        True-up queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Facilities Encamp manages where a CERS filing was submitted outside Encamp in the last 12
        months — our records for these may be stale. Open a row to see exactly what the customer
        filed.
      </Typography>
      <Box>
        <OmnisearchDataGridV2
          query={GET_STAFF_CERS_TRUE_UP_SUBMITTALS}
          getItems={getItems}
          getCount={getCount}
          persistenceKey="staff-cers-coverage-trueup-v2"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          initialSortModel={INITIAL_SORT_MODEL}
          filterGroups={FILTER_GROUPS}
          columns={columns}
          buildVariables={buildVariables}
          onRowClick={onRowClick}
          noRowsMessage="No out-of-band filings in the last 12 months."
        />
      </Box>
      <SubmittalDetailDrawer submittal={selected} onClose={() => setSelected(null)} />
    </Paper>
  );
}
